import { useEffect, useMemo, useRef, useState } from "react";
import { Upload, Loader2, Printer, Trash2, FileSpreadsheet, FileText, Image as ImageIcon, Save, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Card, Pills } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { StockPrint, inchLen } from "@/components/StockPrint";

const GRADES = [
  { value: "4.6", label: "MS 4.6" },
  { value: "8.8", label: "HT 8.8" },
];
const todayISO = () => new Date().toISOString().slice(0, 10);
const num = (v) => Number(v) || 0;

function mergeItems(sources) {
  const map = new Map();
  for (const s of sources) {
    for (const it of s.items) {
      const cur = map.get(it.key);
      if (cur) cur.bags += num(it.bags);
      else map.set(it.key, { ...it, bags: num(it.bags) });
    }
  }
  return [...map.values()];
}

const typeOrder = { bolt: 0, nut: 1, washer: 2, rod: 3, stud: 4, assembled: 5, other: 6 };
const sortItems = (a, b) =>
  (typeOrder[a.type] ?? 9) - (typeOrder[b.type] ?? 9) ||
  (a.plating ? 1 : 0) - (b.plating ? 1 : 0) ||
  (a.system === "mm" ? 1 : 0) - (b.system === "mm" ? 1 : 0) ||
  a.dia_order - b.dia_order ||
  (a.thread === "HT" ? 1 : 0) - (b.thread === "HT" ? 1 : 0) ||
  a.len_value - b.len_value;

const FileIcon = ({ name }) => {
  const n = (name || "").toLowerCase();
  if (/\.(xlsx|xls|csv)$/.test(n)) return <FileSpreadsheet className="h-4 w-4 text-emerald-400" />;
  if (/\.(png|jpe?g|webp|heic)$/.test(n)) return <ImageIcon className="h-4 w-4 text-sky-400" />;
  return <FileText className="h-4 w-4 text-orange-400" />;
};

export default function StockPage() {
  const [grade, setGrade] = useState("4.6");
  const [asOn, setAsOn] = useState(todayISO());
  const [sources, setSources] = useState([]);
  const [items, setItems] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [filter, setFilter] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setItems(null);
    setSources([]);
    setUploads([]);
    api
      .get(`/stock/${grade}`)
      .then(({ data }) => {
        if (!alive) return;
        if (data.items?.length) {
          setItems(data.items);
          setSources(data.sources || []);
          setAsOn(data.as_on || todayISO());
          setSavedAt(data.updated_at);
        } else {
          setAsOn(todayISO());
          setSavedAt(null);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [grade]);

  const onFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    for (const f of list) {
      const id = `${f.name}-${Date.now()}-${Math.random()}`;
      setUploads((u) => [...u, { id, name: f.name, status: "parsing" }]);
      const fd = new FormData();
      fd.append("file", f);
      fd.append("grade", grade);
      try {
        const { data } = await api.post("/stock/parse", fd, { timeout: 300000 });
        setUploads((u) => u.map((x) => (x.id === id ? { ...x, status: "done", count: data.items.length, live: data.rows_with_stock } : x)));
        setSources((s) => {
          const next = [...s.filter((x) => x.filename !== data.filename), { filename: data.filename, source: data.source, items: data.items, count: data.items.length }];
          setItems(mergeItems(next));
          return next;
        });
        toast.success(`${f.name}: ${data.rows_with_stock} sizes in stock (${data.items.length} rows read)`);
      } catch (e) {
        setUploads((u) => u.map((x) => (x.id === id ? { ...x, status: "error", error: apiError(e) } : x)));
        toast.error(`${f.name}: ${apiError(e)}`);
      }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeSource = (filename) => {
    setSources((s) => {
      const next = s.filter((x) => x.filename !== filename);
      setItems(next.length ? mergeItems(next) : null);
      return next;
    });
    setUploads((u) => u.filter((x) => x.name !== filename));
  };

  const updateBags = (key, v) => setItems((arr) => arr.map((it) => (it.key === key ? { ...it, bags: v === "" ? 0 : num(v) } : it)));
  const removeItem = (key) => setItems((arr) => arr.filter((it) => it.key !== key));

  const save = async () => {
    if (!items) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/stock/${grade}`, {
        as_on: asOn,
        sources: sources.map((s) => ({ filename: s.filename, source: s.source, count: s.count, items: s.items })),
        items,
      });
      setSavedAt(data.updated_at);
      toast.success("Stock list saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const [printItems, setPrintItems] = useState(null);

  const doPrint = async () => {
    const other = grade === "8.8" ? "4.6" : "8.8";
    let otherItems = [];
    try {
      const { data } = await api.get(`/stock/${other}`);
      otherItems = (data.items || []).map((it) => ({ ...it, grade: other }));
    } catch {
      otherItems = [];
    }
    const combined = [...(items || []).map((it) => ({ ...it, grade })), ...otherItems];
    if (!otherItems.length) toast.info(`No saved ${other} stock yet — PDF has ${grade} only`);
    setPrintItems(combined);
    setPrinting(true);
    await new Promise((r) => setTimeout(r, 200));
    const done = () => {
      setPrinting(false);
      window.removeEventListener("afterprint", done);
    };
    window.addEventListener("afterprint", done);
    window.print();
    setTimeout(done, 60000);
  };

  const sorted = useMemo(() => (items ? [...items].sort(sortItems) : []), [items]);
  const shown = useMemo(() => {
    const f = filter.trim().toUpperCase();
    return f ? sorted.filter((it) => `${it.label} ${it.name} ${it.plating}`.toUpperCase().includes(f)) : sorted;
  }, [sorted, filter]);
  const live = sorted.filter((it) => it.bags > 0);
  const stats = {
    sizes: live.filter((it) => it.type === "bolt").length,
    zero: sorted.filter((it) => it.bags <= 0).length,
    over20: live.filter((it) => it.bags > 20).length,
    others: live.filter((it) => it.type !== "bolt").length,
  };

  return (
    <div className="space-y-4" data-testid="stock-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-black tracking-tight">Stock List</h1>
          <p className="text-xs text-slate-400">Upload godown / factory sheets → compile → send a clean stock PDF to parties.</p>
        </div>
        <Pills value={grade} options={GRADES} onChange={setGrade} testid="stock-grade" />
      </div>

      {/* Upload */}
      <Card className="space-y-3 p-4">
        <div
          data-testid="stock-dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            onFiles(e.dataTransfer.files);
          }}
          onClick={() => fileRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-700 bg-slate-900/40 px-4 py-6 text-center hover:border-orange-500/60"
        >
          <Upload className="h-6 w-6 text-orange-400" />
          <div className="text-sm font-bold">Upload {grade === "8.8" ? "8.8" : "MS 4.6"} stock sheets</div>
          <div className="text-[11px] text-slate-500">PDF · Excel (.xlsx/.xls) · CSV · photo — add several files, they get added together per size</div>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept=".pdf,.xlsx,.xls,.csv,.txt,image/*"
            className="hidden"
            data-testid="stock-file-input"
            onChange={(e) => onFiles(e.target.files)}
          />
        </div>

        {(uploads.length > 0 || sources.length > 0) && (
          <ul className="space-y-1.5" data-testid="stock-sources">
            {sources.map((s) => (
              <li key={s.filename} className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2 text-xs" data-testid={`stock-source-${s.source}`}>
                <FileIcon name={s.filename} />
                <span className="flex-1 truncate font-semibold">{s.filename}</span>
                <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-300">{s.source}</span>
                <span className="text-slate-400">{s.count} rows</span>
                <button onClick={() => removeSource(s.filename)} className="text-slate-500 hover:text-red-400" data-testid={`remove-source-${s.source}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
            {uploads
              .filter((u) => u.status !== "done")
              .map((u) => (
                <li key={u.id} className="flex items-center gap-2 rounded-lg border border-slate-800 px-3 py-2 text-xs" data-testid="stock-upload-status">
                  {u.status === "parsing" ? <Loader2 className="h-4 w-4 animate-spin text-orange-400" /> : <AlertTriangle className="h-4 w-4 text-red-400" />}
                  <span className="flex-1 truncate">{u.name}</span>
                  <span className={u.status === "error" ? "text-red-400" : "text-slate-400"}>{u.status === "parsing" ? "AI reading…" : u.error}</span>
                </li>
              ))}
          </ul>
        )}
      </Card>

      {items && (
        <>
          {/* Summary + actions */}
          <Card className="space-y-3 p-4" data-testid="stock-summary">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Sizes in stock" value={stats.sizes} testid="stat-sizes" />
              <Stat label="Over 20 bags" value={stats.over20} testid="stat-over20" />
              <Stat label="Nuts / washers / other" value={stats.others} testid="stat-others" />
              <Stat label="Zero qty (hidden in PDF)" value={stats.zero} dim testid="stat-zero" />
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-500">Stock as on</span>
                <Input type="date" value={asOn} onChange={(e) => setAsOn(e.target.value)} className="h-10 w-44 border-slate-700 bg-slate-900/60" data-testid="stock-ason-input" />
              </label>
              <Button onClick={save} disabled={saving} className="h-10 rounded-xl bg-slate-800 font-bold text-slate-100 hover:bg-slate-700" data-testid="stock-save-btn">
                {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Save
              </Button>
              <Button onClick={doPrint} className="h-10 flex-1 rounded-xl bg-orange-500 font-bold text-slate-950 hover:bg-orange-400 sm:flex-none" data-testid="stock-pdf-btn">
                <Printer className="mr-1 h-4 w-4" /> Print / PDF (4.6 + 8.8)
              </Button>
              {savedAt && (
                <span className="flex items-center gap-1 text-[11px] text-emerald-400" data-testid="stock-saved-at">
                  <CheckCircle2 className="h-3.5 w-3.5" /> saved {new Date(savedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </div>
          </Card>

          {/* Review table */}
          <Card className="p-0" data-testid="stock-review">
            <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-2.5">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Review & edit ({shown.length})</span>
              <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter size…" className="ml-auto h-8 w-40 border-slate-700 bg-slate-900/60 text-xs" data-testid="stock-filter-input" />
              <button onClick={() => setFilter("")} className="text-slate-500 hover:text-slate-300" title="Clear filter">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#0d1220] text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Size</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-right">Bags</th>
                    <th className="px-3 py-2 text-right">PDF</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((it) => (
                    <tr key={it.key} className={`border-t border-slate-800/70 ${it.bags <= 0 ? "opacity-40" : ""}`} data-testid={`stock-row-${it.key}`}>
                      <td className="px-3 py-1.5 font-semibold">
                        {it.type === "bolt" ? `${it.system === "mm" ? "M" : ""}${it.dia} ${it.thread} ${inchLen(it.length)}` : it.label}
                        {it.plating ? <span className="ml-1 rounded bg-sky-500/15 px-1 text-[10px] font-bold text-sky-300">{it.plating}</span> : null}
                        {it.note ? <span className="ml-1 text-[11px] text-slate-500">{it.note}</span> : null}
                      </td>
                      <td className="px-3 py-1.5 text-xs uppercase text-slate-500">{it.type}</td>
                      <td className="px-3 py-1.5 text-right">
                        <input
                          inputMode="numeric"
                          value={it.bags}
                          onChange={(e) => updateBags(it.key, e.target.value)}
                          className="h-8 w-20 rounded-md border border-slate-700 bg-slate-900/60 px-2 text-right font-mono text-sm outline-none focus:border-orange-500"
                          data-testid={`stock-bags-${it.key}`}
                        />
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono font-bold text-orange-300">{it.bags > 20 ? ">20" : it.bags > 0 ? it.bags : "—"}</td>
                      <td className="pr-2">
                        <button onClick={() => removeItem(it.key)} className="text-slate-600 hover:text-red-400" data-testid={`stock-remove-${it.key}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {printing && <StockPrint grade={grade} asOn={asOn} items={printItems} />}
    </div>
  );
}

function Stat({ label, value, dim, testid }) {
  return (
    <div className={`rounded-xl border border-slate-800 bg-slate-900/50 px-3 py-2 ${dim ? "opacity-60" : ""}`} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono text-xl font-black text-orange-300">{value}</div>
    </div>
  );
}
